import { CircleNotch, XCircle } from '@phosphor-icons/react';
import { Alert, Button, Dialog, Select, Textarea } from '@fragments-sdk/ui';
import { formatRuleLabel } from '@/lib/govern-rule-catalog';
import type { WaiverDraft, WaiverScope } from './findings-types';
import s from './findings.module.scss';

const WAIVER_EXPIRIES = [
  { label: 'Never', value: 0 },
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
];

export function WaiverDialog({
  draft,
  onChange,
  onCancel,
  onSubmit,
}: {
  draft: WaiverDraft;
  onChange: (next: WaiverDraft) => void;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  const canUsePath = Boolean(draft.finding.filePath);

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
    >
      <Dialog.Content size="sm" className={s.waiverPrimitiveDialog}>
        <Dialog.Header>
          <div>
            <Dialog.Title>Waive finding</Dialog.Title>
            <Dialog.Description>
              {formatRuleLabel(draft.finding.ruleId)}
            </Dialog.Description>
          </div>
          <Dialog.Close />
        </Dialog.Header>

        <Dialog.Body className={s.waiverPrimitiveBody}>
          <Select
            label="Scope"
            value={draft.scope}
            size="sm"
            onValueChange={(value) =>
              onChange({
                ...draft,
                scope: (value ?? 'fingerprint') as WaiverScope,
              })
            }
          >
            <Select.Trigger />
            <Select.Content>
              <Select.Item value="fingerprint">Only this finding</Select.Item>
              <Select.Item value="rule">This rule</Select.Item>
              <Select.Item value="path" disabled={!canUsePath}>
                This file
              </Select.Item>
              <Select.Item value="rule-path" disabled={!canUsePath}>
                This rule in this file
              </Select.Item>
            </Select.Content>
          </Select>

          <Select
            label="Expires"
            value={String(draft.expiresInDays)}
            size="sm"
            onValueChange={(value) =>
              onChange({
                ...draft,
                expiresInDays: Number(value ?? 0),
              })
            }
          >
            <Select.Trigger />
            <Select.Content>
              {WAIVER_EXPIRIES.map((expiry) => (
                <Select.Item key={expiry.value} value={String(expiry.value)}>
                  {expiry.label}
                </Select.Item>
              ))}
            </Select.Content>
          </Select>

          <Textarea
            label="Reason"
            value={draft.reason}
            onValueChange={(reason) => onChange({ ...draft, reason })}
            rows={3}
            placeholder="Optional note for future reviewers"
          />

          <Alert severity="warning" className={s.waiverTargetPreview}>
            <Alert.Body>
              <Alert.Title>Waiver target</Alert.Title>
              <Alert.Content>{formatWaiverPreview(draft)}</Alert.Content>
            </Alert.Body>
          </Alert>

          {draft.error ? (
            <Alert severity="error" className={s.waiverError}>
              <Alert.Body>
                <Alert.Title>Could not create waiver</Alert.Title>
                <Alert.Content>{draft.error}</Alert.Content>
              </Alert.Body>
            </Alert>
          ) : null}
        </Dialog.Body>

        <Dialog.Footer>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            size="sm"
            disabled={draft.submitting}
            onClick={onSubmit}
          >
            {draft.submitting ? (
              <CircleNotch size={13} className={s.spinIcon} />
            ) : (
              <XCircle size={13} />
            )}
            Create waiver
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  );
}

function formatWaiverPreview(draft: WaiverDraft) {
  if (draft.scope === 'rule') return `Suppresses ${draft.finding.ruleId}`;
  if (draft.scope === 'path') return `Suppresses ${draft.finding.filePath}`;
  if (draft.scope === 'rule-path') {
    return `Suppresses ${draft.finding.ruleId} in ${draft.finding.filePath}`;
  }
  return `Suppresses fingerprint ${draft.finding.fingerprint}`;
}
